import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Popover, Select } from "antd";
import { useHistory } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, Sector, ResponsiveContainer } from "recharts";
import { companiesService, dashboardPresencaService, departmentsService } from "./../../../redux/services";
import { palette } from "../../../config/theme";
import "./index.css";

const SITUACOES = [
  { name: "Presentes", color: palette.green500 },
  { name: "Ausentes", color: palette.pink500 },
  { name: "Afastados", color: palette.amber500 },
];

const corDaSituacao = (name) =>
  SITUACOES.find((s) => s.name === name)?.color ?? palette.neutral400;

const isAll = (v) => v === "" || v === "all" || v == null;

const formatPct = (v) => `${v.toFixed(1).replace(".", ",")}%`;

function ActiveSlice(props) {
  return <Sector {...props} outerRadius={props.outerRadius + 8} />;
}

function EmptyDonut() {
  return (
    <div className="byp-presence__empty">
      <svg width="190" height="190" viewBox="0 0 190 190" role="img" aria-label="Nenhum registro encontrado">
        <circle cx="95" cy="95" r="70" fill="none" stroke="var(--byp-neutral-100)" strokeWidth="22" />
        <circle
          cx="95" cy="95" r="70" fill="none"
          stroke="var(--byp-neutral-200)" strokeWidth="22" strokeLinecap="round"
          strokeDasharray="110 330"
        />
      </svg>
      <span>Nenhum registro encontrado</span>
    </div>
  );
}

export default function DonutChart({ empresa = "all", departamento = "" }) {
  const history = useHistory();

  const [emp, setEmp] = useState(empresa);
  const [dept, setDept] = useState(departamento);

  const [modalFilter, setModalFilter] = useState(false);
  const [empDraft, setEmpDraft] = useState(empresa);
  const [deptDraft, setDeptDraft] = useState(departamento);

  const [loadingChart, setLoadingChart] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [empresas, setEmpresas] = useState([{ label: "Todas as empresas", value: "all" }]);

  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [departamentos, setDepartamentos] = useState([{ label: "Todos os Departamentos", value: "" }]);

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  async function carregarDepartamentos(empUuid) {
    setLoadingDepartamentos(true);
    try {
      const options = {};
      if (!isAll(empUuid)) options.empresa_uuid = empUuid;

      const response = await departmentsService.getAll(options);
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }, ...lista]);
    } catch (e) {
      console.error("Erro ao carregar departamentos", e);
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
    } finally {
      setLoadingDepartamentos(false);
    }
  }

  async function carregaDados(nextEmp = emp, nextDept = dept) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa = nextEmp;
      if (!isAll(nextDept)) options.departamento = nextDept;

      const resp = await dashboardPresencaService.getAll(options);

      const presenca =
        resp?.data?.data?.presenca ??
        resp?.data?.presenca ??
        resp?.presenca ??
        [];

      const arr = Array.isArray(presenca) ? presenca : [];
      const porNome = new Map(
        arr.map((it) => [String(it.name ?? it.nome ?? ""), Number(it.value ?? it.total ?? 0)])
      );

      setChartData(SITUACOES.map((s) => ({ name: s.name, value: porNome.get(s.name) ?? 0 })));
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  }

  function openFilter() {
    setEmpDraft(emp || "all");
    setDeptDraft(dept || "");
    setModalFilter(true);
  }

  async function applyFilter() {
    const nextEmp = empDraft || "all";
    const nextDept = deptDraft || "";
    setEmp(nextEmp);
    setDept(nextDept);
    setModalFilter(false);
    await carregaDados(nextEmp, nextDept); // evita estado antigo
  }

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregarDepartamentos("");
      await carregaDados(emp, dept);
    })();
  }, []);

  const skipSyncRef = useRef(true);

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setEmp(empresa);
    setDept(departamento);
    carregaDados(empresa, departamento);
  }, [empresa, departamento]);

  useEffect(() => {
    if (!modalFilter) return;
    if (isAll(empDraft)) {
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
      return;
    }
    carregarDepartamentos(empDraft);
  }, [empDraft, modalFilter]);

  const total = useMemo(
    () => chartData.reduce((acc, d) => acc + (d?.value ?? 0), 0),
    [chartData]
  );
  const hasData = total > 0;
  const taxaPresenca = hasData
    ? ((chartData.find((d) => d.name === "Presentes")?.value ?? 0) / total) * 100
    : 0;

  const filtroContent = (
    <div className="byp-card__filter">
      <label>
        Empresa
        <Select
          value={empDraft}
          onChange={(v) => {
            setEmpDraft(v);
            setDeptDraft("");
          }}
          options={empresas}
          size="middle"
          style={{ width: "100%" }}
          loading={loadingEmpresas}
        />
      </label>
      <label>
        Departamento
        <Select
          value={deptDraft}
          onChange={setDeptDraft}
          options={departamentos}
          size="middle"
          style={{ width: "100%" }}
          loading={loadingDepartamentos}
          disabled={isAll(empDraft)}
          notFoundContent="Nenhum departamento encontrado"
        />
      </label>
      <div className="byp-card__filter-actions">
        <Button size="small" onClick={() => setModalFilter(false)}>Cancelar</Button>
        <Button size="small" type="primary" onClick={applyFilter}>Filtrar</Button>
      </div>
    </div>
  );

  return (
    <section className="byp-card byp-presence">
      <header className="byp-card__head">
        <div>
          <h3 className="byp-card__title">
            <span className="byp-card__title-icon"><i className="fad fa-user-clock" /></span>
            Visão Geral de Presença <span className="byp-card__title-note">(agora)</span>
          </h3>
        </div>
        <div className="byp-card__head-actions">
          <button
            type="button"
            className="byp-card__action"
            onClick={() => history.push("/gestao/dailypoint")}
          >
            Ver tudo <i className="fas fa-chevron-right" />
          </button>
          <Popover
            content={filtroContent}
            trigger="click"
            placement="bottomRight"
            visible={modalFilter}
            onVisibleChange={(v) => (v ? openFilter() : setModalFilter(false))}
            overlayClassName="byp-card__filter-overlay"
          >
            <button type="button" className="byp-card__action byp-card__action--icon" aria-label="Filtrar">
              <i className="far fa-filter" />
            </button>
          </Popover>
        </div>
      </header>

      <div className="byp-presence__body">
        <div className="byp-presence__chart">
          {!hasData ? (
            <EmptyDonut />
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius="72%"
                    outerRadius="94%"
                    paddingAngle={chartData.filter((d) => d.value > 0).length > 1 ? 2 : 0}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                    labelLine={false}
                    activeIndex={activeIndex}
                    activeShape={(props) => <ActiveSlice {...props} />}
                    onMouseEnter={(_, idx) => setActiveIndex(idx)}
                    onMouseLeave={() => setActiveIndex(null)}
                    isAnimationActive
                    animationDuration={500}
                    animationEasing="ease-out"
                  >
                    {chartData.map((d) => (
                      <Cell key={d.name} fill={corDaSituacao(d.name)} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${value} (${formatPct((value / total) * 100)})`,
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="byp-presence__center">
                <i className="fad fa-users" />
                <strong>{Math.round(taxaPresenca)}%</strong>
                <small>Taxa de presença</small>
              </div>
            </>
          )}
        </div>

        <div className="byp-presence__legend">
          <ul>
            {chartData.map((d) => (
              <li key={d.name}>
                <span className="byp-dot" style={{ background: corDaSituacao(d.name) }} />
                <span className="byp-presence__legend-name">{d.name}</span>
                <strong>{d.value}</strong>
                <em>{formatPct(total ? (d.value / total) * 100 : 0)}</em>
              </li>
            ))}
          </ul>

          <div className="byp-presence__total">
            <span>Total de funcionários</span>
            <strong>{total}</strong>
          </div>
        </div>
      </div>

      {loadingChart && (
        <div className="byp-card__loading">Carregando…</div>
      )}
    </section>
  );
}
